import { usePowerSync } from "@powersync/react";
import { useApp } from "@/app/AppContext";
import { api } from "@/auth/session";
import { useToast } from "@/ui/toast";
import { useCelebrate } from "@/ui/celebrate";

// Every write goes through here. Live mode calls the documented Rails endpoints
// (PropelRails docs/api-contract) and the result syncs back through PowerSync.
// Demo mode writes the local demo database only (it never syncs).
export function useMutations() {
  const { mode, userId } = useApp();
  const db = usePowerSync();
  const toast = useToast();
  const celebrate = useCelebrate();
  const demo = mode === "demo";
  const now = () => new Date().toISOString();

  async function run(label: string, live: () => Promise<unknown>, local: () => Promise<unknown>, success?: string) {
    try {
      await (demo ? local() : live());
      if (success) toast({ variant: "success", title: success });
      return true;
    } catch (e: any) {
      toast({ variant: "error", title: `${label} failed`, message: e?.body?.error || e?.message || "Try again in a moment." });
      return false;
    }
  }

  return {
    saveReport: (id: string, reportData: unknown) =>
      run("Save", () => api(`/reports/${id}`, { method: "PATCH", body: JSON.stringify({ field_note_report: { report_data: JSON.stringify(reportData) } }) }),
        () => db.execute("UPDATE mobile_custom_report_statuses SET report_data = ? WHERE id = ?", [JSON.stringify(reportData), id]), "Report saved"),

    approveReport: async (id: string, approverName: string, signatureData: string, where?: string) => {
      const ok = await run("Sign", () => api(`/reports/${id}/approve`, { method: "POST", body: JSON.stringify({ signature_data: signatureData, approver_name: approverName }) }),
        () => db.execute("UPDATE mobile_custom_report_statuses SET approved_at = ?, approver_name = ?, signature_data = ? WHERE id = ?", [now(), approverName, signatureData, id]));
      if (ok) celebrate({ kind: "visit", title: "Visit wrapped up", sub: [where, "report signed"].filter(Boolean).join(" · ") });
      return ok;
    },

    downloadReport: async (id: string) => {
      if (demo) { toast({ variant: "info", title: "Downloads use the live backend", message: "The demo team has no generated documents." }); return; }
      try {
        const r = await api<{ url: string }>(`/reports/${id}/download`);
        window.open(r.url, "_blank", "noopener");
      } catch (e: any) {
        toast({ variant: "error", title: "Download not ready", message: e?.body?.error || "The document may still be generating." });
      }
    },

    completeReminder: async (id: string, remaining?: number) => {
      const ok = await run("Complete", () => api(`/scheduled_reminders/${id}/complete`, { method: "POST" }),
        () => db.execute("UPDATE mobile_reminders SET status = 1, completed_at = ?, completed_by_id = ? WHERE id = ?", [now(), userId, id]));
      if (ok) celebrate({ kind: "task", title: "Task done", sub: remaining === undefined ? undefined : remaining === 0 ? "Nothing left for today" : `${remaining} left today` });
      return ok;
    },

    reinstateReminder: (id: string) =>
      run("Reopen", () => api(`/scheduled_reminders/${id}/reinstate`, { method: "POST" }),
        () => db.execute("UPDATE mobile_reminders SET status = 0, completed_at = NULL, canceled_at = NULL WHERE id = ?", [id])),

    // Task = scheduled reminder. Title + notes share `details` (first line is the
    // title). reminder_type: 0 to-do, 1 follow-up, 2 appointment. Teammate
    // assignees (`user_ids`) are sent for forward compatibility: today the API
    // path assigns the creator only (scheduled_reminders_controller#create_from_api).
    createReminder: async (r: { locationId: string; details?: string; title?: string; notes?: string; date: string; time?: string; visitId?: string | null; type?: 0 | 1 | 2; assigneeIds?: string[] }): Promise<string | null> => {
      const id = crypto.randomUUID();
      const details = r.details ?? [r.title?.trim(), r.notes?.trim()].filter(Boolean).join("\n\n");
      const ok = await run("Create task",
        () => api(`/map_features/${r.locationId}/scheduled_reminders`, { method: "POST", body: JSON.stringify({ id, details, reminder_date: r.date, reminder_time: r.time ? `${r.time}:00` : undefined, reminder_type: r.type ?? 0, field_note_id: r.visitId ?? undefined, user_ids: r.assigneeIds }) }),
        () => db.execute("INSERT INTO mobile_reminders (id, details, reminder_date, reminder_time, reminder_type, map_feature_id, field_note_id, creator_user_id, assigned_user_id, status, ai_created, created_at, updated_at) VALUES (?,?,?,?,?,?,?,?,?,0,0,?,?)",
          [id, details, r.date, r.time ? `${r.time}:00` : null, r.type ?? 0, r.locationId, r.visitId ?? null, userId, r.assigneeIds?.[0] ?? userId, now(), now()]), "Task created");
      return ok ? id : null;
    },

    cancelReminder: (id: string) =>
      run("Cancel task", () => api(`/scheduled_reminders/${id}/cancel`, { method: "POST" }),
        () => db.execute("UPDATE mobile_reminders SET status = 4, canceled_at = ?, canceled_by_id = ? WHERE id = ?", [now(), userId, id]), "Task canceled"),

    deleteReminder: (id: string) =>
      run("Delete task", () => api(`/scheduled_reminders/${id}`, { method: "DELETE" }),
        () => db.execute("DELETE FROM mobile_reminders WHERE id = ?", [id]), "Task deleted"),

    addComment: (c: { locationId: string; visitId?: string | null; content: string }) => {
      const id = crypto.randomUUID();
      return run("Comment",
        () => api("/api/v1/map_feature_comments", { method: "POST", body: JSON.stringify({ map_feature_comment: { id, map_feature_id: c.locationId, field_note_id: c.visitId ?? null, content: c.content } }) }),
        () => db.execute("INSERT INTO mobile_map_feature_comments (id, content, map_feature_id, field_note_id, user_id, status, ai_generated, created_at) VALUES (?,?,?,?,?,'approved',0,?)",
          [id, c.content, c.locationId, c.visitId ?? null, userId, now()]));
    },
  };
}
